import { observer } from "mobx-react-lite";
import React from "react";
import { Text, XStack, YStack } from "tamagui";
import { Body } from "../../../ui/Body";
import { Group } from "../../../ui/Group";
import { IconBox } from "../../../ui/IconBox";
import { Label } from "../../../ui/Label";
import { Sheet, type SheetProps } from "../../../ui/Sheet";
import { TipsText } from "../../../ui/TipsText";
import { Title } from "../../../ui/Title";
import type { BusinessModuleTemplateData } from "../../admin/templates/types";
import { allBusinessModulesAvailable } from "../allBusinessModules";
import type { BusinessInfoData } from "../BusinessInfoModule/BusinessInfoModel";
import type { BusinessReminderData } from "../BusinessReminder/BusinessReminderModuleModel";
import {
  type BusinessModuleMetaCategory,
  type BusinessModuleMetaData,
  BusinessModuleType,
} from "../types";

export const CardsList: React.FC<{
  category: BusinessModuleMetaCategory;
  onPress: (module: BusinessModuleMetaData) => void;
  onClose: () => void;
}> = observer(({ category, onPress }) => {
  const cards = category.modules;

  const Icon = category.icon;

  return (
    <YStack gap="$space.small" marginBottom="$larger">
      {category.title && (
        <XStack gap="$medium" alignItems="center" marginLeft="$small">
          {Icon && <Icon color="$muted" />}
          <Label color={"$muted"}>{category.title}</Label>
        </XStack>
      )}
      <Group borderTop={!!category.title}>
        {cards.map((module, i) => {
          return (
            <IconBox
              key={`_${i}`}
              onPress={async () => {
                onPress(module);
              }}
              grouped
              icon={() => (
                <Text lineHeight={24} fontSize={24}>
                  {module.emoji}
                </Text>
              )}
              label={module.title}
            >
              <Body>{module.description}</Body>
            </IconBox>
          );
        })}
      </Group>
    </YStack>
  );
});

export const BusinessAddCardSheet: React.FC<
  SheetProps & {
    overrideTypes?: BusinessModuleType[];
    onSave: (data: Omit<BusinessModuleTemplateData, "uid">) => void;
  }
> = observer(({ onOpenChange, onSave, overrideTypes, ...props }) => {
  const [createModule, setCreateModule] = React.useState<
    BusinessModuleMetaData | undefined
  >(undefined);

  React.useEffect(() => {
    // Create module at once, no popup
    if (createModule) {
      let moduleData: Omit<BusinessModuleTemplateData, "uid"> | undefined;

      if (createModule.type === BusinessModuleType.Reminder) {
        moduleData = {
          type: createModule.type,
          emoji: null,
          title: "My reminder",
          descriptionYooptaEditor: null,
          settings: {
            dateDue: null,
            offsetInDays: null,
          },
        } as Omit<BusinessReminderData, "uid">;
      } else if (createModule.type === BusinessModuleType.Info) {
        moduleData = {
          type: createModule.type,
          emoji: null,
          title: "My info",
          descriptionYooptaEditor: null,
        } as Omit<BusinessInfoData, "uid">;
      } else {
        throw Error("Invalid module type!");
      }

      onSave(moduleData);
      setCreateModule(undefined);
    }
  }, [createModule, onSave]);

  return (
    <Sheet
      {...props}
      onOpenChange={onOpenChange}
      header={
        <YStack gap="$space.small">
          <Title>Add task</Title>
          <TipsText text="Select a task to start using it" />
        </YStack>
      }
    >
      {allBusinessModulesAvailable.map((moduleCategory, i) => {
        const filteredCategory = { ...moduleCategory };

        if (overrideTypes) {
          filteredCategory.modules = filteredCategory.modules.filter((m) =>
            overrideTypes.includes(m.type),
          );
        }

        return (
          <CardsList
            key={`category_${i}`}
            category={filteredCategory}
            onPress={async (module) => {
              setCreateModule(module);
              onOpenChange?.(false);
            }}
            onClose={() => onOpenChange?.(false)}
          />
        );
      })}
    </Sheet>
  );
});

/*
  const formValues = useMemo(
    () => ({ emoji: createModule?.emoji ?? "" }),
    [createModule?.emoji],
  );


 {!!createModule && (
        <FormDataModal
          onClose={() => setCreateModule(undefined)}
          visible={!!createModule}
          title={createModule?.title ?? ""}
          description={createModule?.description}
          form={[
            {
              formKey: "title",
              label: "Title",
              type: FormSheetType.Input,
              icon: Type,
              placeholder: "Title...",
            },
            {
              formKey: "emoji",
              label: "Icon",
              type: FormSheetType.Emoji,
              icon: Smile,
              placeholder: "Select icon...",
            },
            ...(createModule.extraData ?? []),
          ]}
          values={{ ...formValues, ...createModule.formValues }}
          onSave={async (data) => {
            if (!createModule || !isCreateModule(data)) {
              return;
            }

            const moduleData: BusinessModuleTemplateData = {
              type: createModule.type,
              ...(createModule.prepareData
                ? createModule.prepareData(data)
                : data),
            };

            onSave(moduleData);
          }}
        />
      )}
 */
